import { LogManager } from '@src/utility/Logger';
import { pickUnderlyingVideoElement } from '@src/utility/stackedLayout';
import { v4 as uuid } from 'uuid';

import { SceneService } from '../../services/SceneService';
import { TimelineMode } from '../commands/TimelineMode';
import { ClipTranscriptSegment } from '../models/ClipTranscriptSegment';
import { Element } from '../models/Element';
import { RemovedTranscriptClip } from '../models/elements/RemovedTranscriptClip';
import { areVideoElementsContinuous } from '../models/elements/VideoElement';
import { VideoTranscriptElement } from '../models/elements/VideoTranscriptElement';
import { TranscriptTrack } from '../models/TranscriptTrack';
import { IdGenerator, IdGeneratorEntityType } from '../services/IdGenerator';
import {
  removePunctuationRange,
  restorePunctuationRange,
} from '../services/punctuation';
import { EditorChangeType } from '../types';

import type { VideoEditor } from '..';
import type { AudioElement } from '../models';
import type { HasClipTranscriptSegment } from '../models/elements/HasClipTranscriptSegment';
import type { VideoElement } from '../models/elements/VideoElement';
import type { ExtraElementData } from '../models/ExtraElementData';
import type { TranscriptToken } from '../types';
import type { TrackManager } from './TrackManager';

/**
 * Default audio fade (in seconds) applied at the outer edges of the primary
 * video track and at non-continuous cuts, to avoid an audible click/pop.
 */
export const DEFAULT_AUDIO_FADE_AT_CUTS = 0.05;

/**
 * TrackManager is a utility class responsible for managing tracks and elements
 * within a Timeline in the video editing context.
 *
 * It provides functionality to create and remove tracks, add or remove elements,
 * merge multiple elements into one, and split an element into multiple parts.
 * The class integrates with the VideoEditor's batching mechanism to optimize
 * performance and maintain state consistency.
 */
export class TranscriptTrackManager {
  /**
   * Instance variables for Timeline and VideoEditor
   */
  protected editor: VideoEditor;
  protected trackManager: TrackManager;

  protected _extraElementData: ExtraElementData | null = null;

  private readonly sceneService = new SceneService();

  /**
   * Creates a new TrackManager instance
   * @param editor Optional VideoEditor reference for dirty marking and notifications
   */
  constructor(editor: VideoEditor, trackManager: TrackManager) {
    this.editor = editor;
    this.trackManager = trackManager;
  }

  /**
   * Applies a default audio fade at every non-continuous cut on the primary
   * video (transcript) track. Continuous boundaries (scene splits of the same
   * source) are left untouched so the audio stays seamless; outer edges and
   * real cuts receive a short fade to avoid an audible click/pop.
   *
   * Fill-nulls-only: elements that already carry an explicit fade value are
   * never overwritten, so user-set fades are preserved.
   *
   * Scope: only the primary video/transcript track's video elements. Overlays,
   * b-roll on other tracks, and music are intentionally untouched.
   */
  public applyDefaultAudioFades(): void {
    const transcriptTrack = this.editor.getVideoTranscriptTrack();
    if (!transcriptTrack) {
      return;
    }

    const videoElements = transcriptTrack.elements
      .filter((el): el is VideoTranscriptElement => el.type === 'video')
      .slice()
      .sort((a, b) => a.time - b.time);

    this.applyDefaultAudioFadesAtCuts(
      videoElements,
      DEFAULT_AUDIO_FADE_AT_CUTS,
    );
  }

  /**
   * Applies a default audio fade at the *outer* edges of the sequence and at any
   * non-continuous cut between adjacent elements. Continuous boundaries are left
   * untouched (no fade), since the audio is seamless there and a fade would
   * introduce an audible dip.
   *
   * Fill-nulls-only: an element that already carries an explicit fade value
   * (including `0`) is never overwritten.
   *
   * @param videoElements Time-ordered video elements (caller sorts by `time`).
   * @param fadeValue     Fade duration in seconds to apply at cuts.
   */
  private applyDefaultAudioFadesAtCuts(
    videoElements: VideoElement[],
    fadeValue: number = DEFAULT_AUDIO_FADE_AT_CUTS,
  ): void {
    for (let i = 0; i < videoElements.length; i++) {
      const element = videoElements[i];
      const prev = i > 0 ? videoElements[i - 1] : null;
      const next = i < videoElements.length - 1 ? videoElements[i + 1] : null;

      if (
        element.audio_fade_in == null &&
        (prev == null || !areVideoElementsContinuous(prev, element))
      ) {
        element.audio_fade_in = fadeValue;
      }

      if (
        element.audio_fade_out == null &&
        (next == null || !areVideoElementsContinuous(element, next))
      ) {
        element.audio_fade_out = fadeValue;
      }
    }
  }

  /**
   * Moves an element to a new position on the timeline
   * @param element Element to move
   * @param newStartTime New start time for the element
   * @param targetTrack Optional target track if moving between tracks
   * @param mode Optional TimelineMode to use for this operation
   * @returns true if successful, false otherwise
   */
  public shiftElement(
    element: VideoTranscriptElement,
    newStartTime: number,
    mode: TimelineMode = TimelineMode.DEFAULT,
    snapToFrame: boolean = true,
  ):
    | {
        result: 'no_change' | 'shift' | 'cut_paste';
        pasted?: VideoTranscriptElement;
      }
    | false {
    if (!element) return false;

    // Get source track
    const sourceTrack = this.editor.timeline.getTrackForElement(element.id);
    if (!sourceTrack || !(sourceTrack instanceof TranscriptTrack)) return false;

    // Snap time to frame and ensure it's non-negative
    if (snapToFrame) {
      newStartTime = this.editor.timeline.snapTimeToFrame(newStartTime);
    }
    newStartTime = Math.max(0, newStartTime);

    // Determine final position based on timeline mode
    let finalStartTime =
      mode === TimelineMode.OVERLAP ||
      (mode === TimelineMode.RIPPLE && newStartTime > element.time) // RIPPLE is possible only when moving to the right
        ? newStartTime
        : this.trackManager.calculateValidStartTime(
            sourceTrack,
            element,
            newStartTime,
            element.time,
            element.durationSeconds,
          );

    // Check if movement is necessary
    const shiftDistance = finalStartTime - element.time;

    // case 1: element not moving (shiftDistance = 0)
    if (shiftDistance === 0) {
      return { result: 'no_change' };
    }

    // case 2: element moving but its position on transcript track does not change
    let result: {
      result: 'shift' | 'cut_paste';
      pasted?: VideoTranscriptElement;
    };
    if (
      this.isOrderingKept(sourceTrack, element, finalStartTime, shiftDistance)
    ) {
      // Just change the time
      this.trackManager.shiftElement(
        element,
        finalStartTime,
        mode,
        undefined, // targetTrackNumber - not changing tracks
        snapToFrame,
      );
      result = { result: 'shift' };
    } else {
      // main case: element moving and its position on transcript track changes
      const segment = element.getClipTranscriptSegment();
      // Move: placeholder must not share ids with the re-inserted VTE below.
      this.removeElement(element, TimelineMode.DEFAULT, {
        preserveTokenIds: false,
      });

      // Insert before the video element
      const insertedElement = this.insertTranscriptSegmentsAtTime(
        [segment],
        finalStartTime,
        element,
        mode,
        { preserveTokenIds: true },
      );
      result = { result: 'cut_paste', pasted: insertedElement![0] };
    }

    return result;
  }

  private isOrderingKept(
    track: TranscriptTrack,
    element: VideoTranscriptElement,
    newStartTime: number,
    shiftDistance: number,
  ): boolean {
    // Only compare video elements to check if order is kept, ignore removed segments
    let insertionIndex = this.findInsertionIndex(
      track.elements,
      newStartTime,
      -1,
    );
    const originalIndex = track.elements.indexOf(element);

    // just check if there are no visible elements after the original index
    if (insertionIndex === -1) {
      return originalIndex === track.elements.length - 1;
    }

    // adjust to the left by one since insertion position accounts for the element itself if moving to the right
    insertionIndex -= shiftDistance > 0 ? 1 : 0;

    return insertionIndex === originalIndex;
  }

  public changeElementPlacements(
    placements: {
      element: VideoTranscriptElement;
      time: number;
    }[],
  ): boolean {
    if (placements.length === 0) return true;
    const sourceTrack = this.editor.timeline.getTrackForElement(
      placements[0].element.id,
    );
    if (!sourceTrack || !(sourceTrack instanceof TranscriptTrack)) return false;

    // Move: placeholders must not share ids with the VTEs re-inserted below.
    for (const { element } of placements) {
      this.removeElement(element, TimelineMode.DEFAULT, {
        preserveTokenIds: false,
      });
    }

    // insert to new places
    for (const { element, time } of placements) {
      const segment = element.getClipTranscriptSegment();
      // Insert before the video element
      const insertedElements = this.insertTranscriptSegmentsAtTime(
        [segment],
        time,
        element,
        TimelineMode.DEFAULT,
        { preserveTokenIds: true },
      );
      if (!insertedElements || insertedElements.length !== 1) {
        console.warn(
          'Expected one inserted element, got',
          insertedElements?.length,
        );
        return false;
      }
      insertedElements[0].setId(element.id); // preserve original id
    }

    // rebuild track after placements changed because it is hard and unreliable to re-combine existing segments
    const newTranscriptTrack = TranscriptTrack.fromVideoTrackAndTranscript(
      sourceTrack,
      this.editor.getFullTranscript(),
      this.editor.getOriginalTranscript()!,
    );

    this.editor.timeline.replaceTrack(sourceTrack.id, newTranscriptTrack);

    return true;
  }

  /**
   * Trims the start of an element, adjusting start time and duration
   * Handles both general and video-specific trimming logic
   * @param element The element to trim
   * @param newStartTime New start time for the element
   * @param mode Optional TimelineMode to use for this operation
   * @returns true if successful, false otherwise
   */
  public trimElementStart(
    element: Element,
    newStartTime: number,
    mode: TimelineMode = TimelineMode.DEFAULT,
    snapToFrame: boolean = true,
  ): ClipTranscriptSegment | null {
    if (!element || !(element instanceof VideoTranscriptElement)) return null;

    // Get track
    const track = this.editor.timeline.getTrackForElement(element.id);
    if (!track || !(track instanceof TranscriptTrack)) return null;

    // Store original transcript segment and start time before any modifications
    const originalSegment = element.getClipTranscriptSegment();
    const originalMediaStart = element.getMediaStartTime();

    // Call super class method to validate, adjust, and update video properties
    const success = this.trackManager.trimElementStart(
      element,
      newStartTime,
      mode,
      snapToFrame,
    );
    if (!success) return null;

    // Determine operation direction based on new vs original media start
    const newMediaStart = element.getMediaStartTime();

    if (newMediaStart > originalMediaStart) {
      // Trimming: new start is later than original start
      return this.calculateAndUpdateTranscriptSegmentsForTrimStart(
        element,
        originalSegment,
        track,
      );
    } else {
      // Extending: new start is earlier than original start
      return this.calculateAndUpdateTranscriptSegmentsForExtendStart(
        element,
        originalSegment,
        track,
      );
    }
  }

  /**
   * Calculates and updates transcript segments for element end trimming
   * @param element Element that was trimmed (should have updated video properties)
   * @param track Transcript track containing the element
   * @returns true if successful
   */
  private calculateAndUpdateTranscriptSegmentsForTrimEnd(
    element: VideoTranscriptElement,
    track: TranscriptTrack,
  ): ClipTranscriptSegment | null {
    const transcriptSegmentIndex = this.findTranscriptElementPosition(
      track,
      element,
    );
    const nextElement =
      track.transcriptSegmentElements[transcriptSegmentIndex + 1];

    // Trimming the end - split current segment
    const [segmentToKeep, segmentToTrim] = this.createSplitTranscriptSegments(
      element.getClipTranscriptSegment()!,
      element.durationSeconds,
    );

    segmentToTrim.markElementsAsRemoved();
    const newSegmentToKeep = segmentToKeep;
    let newRemovedSegment: ClipTranscriptSegment;

    if (nextElement instanceof RemovedTranscriptClip) {
      newRemovedSegment = segmentToTrim.concat(
        nextElement.getClipTranscriptSegment(),
      );
    } else {
      newRemovedSegment = segmentToTrim;
    }

    // Apply transcript segment updates
    element.setClipTranscriptSegment(newSegmentToKeep);
    if (nextElement instanceof RemovedTranscriptClip) {
      // Update existing RemovedTranscriptClip with merged/trimmed segment
      nextElement.setClipTranscriptSegment(newRemovedSegment);
    } else {
      // Create a new RemovedTranscriptClip if next element is not a removed segment
      const newRemovedElement = new RemovedTranscriptClip({
        clipTranscriptSegment: newRemovedSegment,
      });
      track.addTranscriptSegmentElement(
        newRemovedElement,
        transcriptSegmentIndex + 1,
      );
    }

    return new ClipTranscriptSegment(
      segmentToTrim.getWordsWithPunctuation(),
      segmentToTrim.mediaStart,
      segmentToTrim.mediaEnd,
    );
  }

  /**
   * Calculates and updates transcript segments for media end extension
   * Handles both restoration from removed segments and addition from original transcript
   * @param element Element that was extended (should have updated video properties)
   * @param originalEndTime Original end time before extension
   * @param track Transcript track containing the element
   * @returns true if successful
   */
  private calculateAndUpdateTranscriptSegmentsForExtendEnd(
    element: VideoTranscriptElement,
    originalEndTime: number,
    track: TranscriptTrack,
  ): ClipTranscriptSegment | null {
    const transcriptSegmentIndex = this.findTranscriptElementPosition(
      track,
      element,
    );

    // Calculate how much we extended based on updated element
    const currentEndTime = element.end_time;
    const extendDiff = currentEndTime - originalEndTime;

    // Get the next removed segment to extend into
    const nextRemovedTranscriptClip =
      track.transcriptSegmentElements[transcriptSegmentIndex + 1];

    const currentSegment = element.getClipTranscriptSegment()!;
    let segmentToRestore: ClipTranscriptSegment | null = null;
    let segmentToKeepRemoved: ClipTranscriptSegment | null = null;

    // Determine available duration from adjacent removed segment (0 if none or not adjacent)
    let availableDuration = 0;
    if (
      nextRemovedTranscriptClip &&
      nextRemovedTranscriptClip instanceof RemovedTranscriptClip
    ) {
      const nextRemovedSegment =
        nextRemovedTranscriptClip.getClipTranscriptSegment()!;
      const isAdjacent =
        Math.abs(nextRemovedSegment.mediaStart - currentSegment.mediaEnd) <
        0.01; // Allow for small floating point differences
      // TODO: implement precise arithmetics

      if (isAdjacent) {
        availableDuration =
          nextRemovedSegment.mediaEnd - nextRemovedSegment.mediaStart;

        if (availableDuration > 0) {
          const durationToRestore = Math.min(availableDuration, extendDiff);

          if (durationToRestore >= availableDuration) {
            // Use entire removed segment
            segmentToRestore = nextRemovedSegment;
            segmentToKeepRemoved = null;
          } else {
            // Split the removed segment
            [segmentToRestore, segmentToKeepRemoved] =
              this.createSplitTranscriptSegments(
                nextRemovedSegment,
                durationToRestore,
              );
          }

          // Mark the restored part as restored
          segmentToRestore.markElementsAsRestored();
        }
      }
    }

    // Create additional segment from original transcript if needed
    const remainingExtension = extendDiff - availableDuration;
    if (remainingExtension > 0) {
      const fromOriginalStart = currentSegment.mediaEnd + availableDuration;
      const fromOriginalEnd = fromOriginalStart + remainingExtension;

      // Ensure we don't exceed media duration
      const maxMediaEnd = Math.min(fromOriginalEnd, element.media_duration!);

      const originalTranscript = this.editor.getOriginalTranscript();
      if (!originalTranscript) {
        return null;
      }

      const segmentFromOriginal = ClipTranscriptSegment.createFromElements(
        originalTranscript as TranscriptToken[],
        fromOriginalStart,
        maxMediaEnd,
        fromOriginalStart,
        maxMediaEnd,
      );

      // Mark elements from original transcript as 'added'
      segmentFromOriginal.getWordsWithPunctuation().forEach(el => {
        el.state = 'added';
      });

      // Merge with restored segment or use as the only segment
      segmentToRestore = segmentToRestore
        ? segmentToRestore.concat(segmentFromOriginal)
        : segmentFromOriginal;
    }

    if (!segmentToRestore) {
      return null;
    }

    // Create new merged segment for the element
    const newSegment = currentSegment.concat(segmentToRestore);

    // Update element transcript segment
    element.setClipTranscriptSegment(newSegment);

    // Update the next removed segment if it exists and was modified
    if (
      nextRemovedTranscriptClip &&
      nextRemovedTranscriptClip instanceof RemovedTranscriptClip &&
      segmentToKeepRemoved
    ) {
      nextRemovedTranscriptClip.setClipTranscriptSegment(segmentToKeepRemoved);
    } else if (
      nextRemovedTranscriptClip &&
      nextRemovedTranscriptClip instanceof RemovedTranscriptClip
    ) {
      track.removeTranscriptSegmentElements(transcriptSegmentIndex + 1, 1);
    }

    return new ClipTranscriptSegment(
      segmentToRestore.getWordsWithPunctuation(),
      segmentToRestore.mediaStart,
      segmentToRestore.mediaEnd,
    );
  }

  /**
   * Adjusts the end of an element (both extending and trimming)
   * Handles both general and video-specific duration adjustment logic
   * @param element The element to adjust
   * @param newEndTime New end time for the element
   * @param mode Optional TimelineMode to use for this operation
   * @returns true if successful, false otherwise
   */
  public trimElementEnd(
    element: VideoTranscriptElement,
    newEndTime: number,
    mode: TimelineMode = TimelineMode.DEFAULT,
    snapToFrame: boolean = true,
  ): ClipTranscriptSegment | null {
    if (!element) return null;

    const track = this.editor.getVideoTranscriptTrack();
    if (!track) return null;

    // Store original end time before any modifications
    const originalEndTime = element.end_time;

    // limit new duration by media time left
    // element.media_duration is maximum value that element.mediaEndTime can be
    // todo revisit
    const maxDuration = element.media_duration! - element.getMediaStartTime();
    newEndTime = Math.min(newEndTime, element.time + maxDuration);

    // Call super class method to validate, adjust, and update video properties
    const success = this.trackManager.trimElementEnd(
      element,
      newEndTime,
      mode,
      snapToFrame,
    );
    if (!success) return null;

    // Determine operation direction based on new vs original end time
    const currentEndTime = element.end_time;

    if (currentEndTime < originalEndTime) {
      // Trimming: new end is earlier than original end
      return this.calculateAndUpdateTranscriptSegmentsForTrimEnd(
        element,
        track,
      );
    } else {
      // Extending: new end is later than original end
      return this.calculateAndUpdateTranscriptSegmentsForExtendEnd(
        element,
        originalEndTime,
        track,
      );
    }
  }

  /**
   * Calculates and updates transcript segments for media start extension
   * Handles both restoration from removed segments and addition from original transcript
   * @param element Element that was extended (should have updated video properties)
   * @param originalSegment Original transcript segment before extension
   * @param track Transcript track containing the element
   * @returns true if successful
   */
  private calculateAndUpdateTranscriptSegmentsForExtendStart(
    element: VideoTranscriptElement,
    originalSegment: ClipTranscriptSegment,
    track: TranscriptTrack,
  ): ClipTranscriptSegment | null {
    const transcriptSegmentIndex = this.findTranscriptElementPosition(
      track,
      element,
    );

    // Calculate how much we extended based on updated element
    const newMediaTrimStart = element.getMediaStartTime();
    const originalTrimStart = originalSegment.mediaStart;
    const trimDiff = originalTrimStart - newMediaTrimStart;

    // Get the previous removed segment to extend from
    const prevRemovedTranscriptClip =
      track.transcriptSegmentElements[transcriptSegmentIndex - 1];

    let segmentToRestore: ClipTranscriptSegment | null = null;
    let segmentToKeepRemoved: ClipTranscriptSegment | null = null;

    // Determine available duration from adjacent removed segment (0 if none or not adjacent)
    let availableDuration = 0;
    if (
      prevRemovedTranscriptClip &&
      prevRemovedTranscriptClip instanceof RemovedTranscriptClip
    ) {
      const prevRemovedSegment =
        prevRemovedTranscriptClip.getClipTranscriptSegment()!;
      const isAdjacent =
        Math.abs(prevRemovedSegment.mediaEnd - originalSegment.mediaStart) <
        0.01; // Allow for small floating point differences

      if (isAdjacent) {
        availableDuration =
          prevRemovedSegment.mediaEnd - prevRemovedSegment.mediaStart;

        if (availableDuration > 0) {
          const durationToRestore = Math.min(availableDuration, trimDiff);

          // TODO: use precise math because >= doesn't work reliably
          if (durationToRestore >= availableDuration) {
            // Use entire removed segment
            segmentToRestore = prevRemovedSegment;
            segmentToKeepRemoved = null;
          } else {
            // Split the removed segment
            const splitTime = prevRemovedSegment.mediaEnd - durationToRestore;
            const splitTimeRelative = splitTime - prevRemovedSegment.mediaStart;
            [segmentToKeepRemoved, segmentToRestore] =
              this.createSplitTranscriptSegments(
                prevRemovedSegment,
                splitTimeRelative,
              );
          }

          // Mark the restored part as restored
          segmentToRestore.markElementsAsRestored();
        }
      }
    }

    // Create additional segment from original transcript if needed
    const remainingExtension = trimDiff - availableDuration;
    if (remainingExtension > 0) {
      const fromOriginalEnd = originalSegment.mediaStart - availableDuration;
      const fromOriginalStart = fromOriginalEnd - remainingExtension;

      // Ensure we don't go below 0 (trim_start limit)
      const minMediaStart = Math.max(fromOriginalStart, 0);

      const originalTranscript = this.editor.getOriginalTranscript();
      if (!originalTranscript) {
        return null;
      }

      const segmentFromOriginal = ClipTranscriptSegment.createFromElements(
        originalTranscript as TranscriptToken[],
        minMediaStart,
        fromOriginalEnd,
        minMediaStart,
        fromOriginalEnd,
      );

      // Mark elements from original transcript as 'added'
      segmentFromOriginal.getWordsWithPunctuation().forEach(el => {
        el.state = 'added';
      });

      // Merge with restored segment or use as the only segment
      segmentToRestore = segmentToRestore
        ? segmentFromOriginal.concat(segmentToRestore)
        : segmentFromOriginal;
    }

    if (!segmentToRestore) {
      return null;
    }

    // Create new merged segment for the element
    const newSegment = segmentToRestore.concat(originalSegment);

    // Update element transcript segment
    element.setClipTranscriptSegment(newSegment);

    // Update the previous removed segment if it exists and was modified
    if (
      prevRemovedTranscriptClip &&
      prevRemovedTranscriptClip instanceof RemovedTranscriptClip &&
      segmentToKeepRemoved
    ) {
      prevRemovedTranscriptClip.setClipTranscriptSegment(segmentToKeepRemoved);
    } else if (
      prevRemovedTranscriptClip &&
      prevRemovedTranscriptClip instanceof RemovedTranscriptClip
    ) {
      track.removeTranscriptSegmentElements(transcriptSegmentIndex - 1, 1);
    }

    return new ClipTranscriptSegment(
      newSegment.getWordsWithPunctuation(),
      newSegment.mediaStart,
      newSegment.mediaEnd,
    );
  }

  /**
   * Adjusts transcript element media start time (both trimming and extending)
   * Automatically detects whether to trim or extend based on comparing new vs current media start
   * - If newMediaTrimStart > current: Trim (remove content from start)
   * - If newMediaTrimStart < current: Extend (restore content from previous removed segment)
   * - If newMediaTrimStart = current: No operation
   * @param element The VideoTranscriptElement to adjust
   * @param newMediaTrimStart New media start time for the element
   * @param mode Optional TimelineMode to use for this operation
   * @param snapToFrame Whether to snap time to frame boundaries
   * @returns true if successful, false otherwise
   */
  public adjustTranscriptElementMediaStart(
    element: VideoTranscriptElement,
    newMediaTrimStart: number,
    mode: TimelineMode = TimelineMode.DEFAULT,
    snapToFrame: boolean = true,
  ): ClipTranscriptSegment | null {
    if (!element) return null;

    const track = this.editor.getVideoTranscriptTrack();
    if (!track) return null;

    const originalTrimStart = element.getMediaStartTime();

    // No change needed
    if (newMediaTrimStart === originalTrimStart) return null;

    // Store original transcript segment before any modifications
    const originalSegment = element.getClipTranscriptSegment();

    // Update video element properties using super class method
    const success = this.trackManager.trimMediaStart(
      element,
      newMediaTrimStart,
      mode,
      snapToFrame,
    );
    if (!success) return null;

    // Choose the appropriate transcript segment calculation based on direction
    if (newMediaTrimStart > originalTrimStart) {
      // Trimming: removing content from start
      return this.calculateAndUpdateTranscriptSegmentsForTrimStart(
        element,
        originalSegment,
        track,
      );
    } else {
      // Extending: restoring content from previous removed segment
      return this.calculateAndUpdateTranscriptSegmentsForExtendStart(
        element,
        originalSegment,
        track,
      );
    }
  }

  /**
   * Creates two new elements from a split operation
   * @param element The original element to split
   * @param splitTime The time where the split occurs
   * @returns Tuple of two new elements (before and after the split)
   */
  private createSplitTranscriptSegments(
    segment: ClipTranscriptSegment,
    relativeSplitTime: number,
  ): [ClipTranscriptSegment, ClipTranscriptSegment] {
    // TODO: try rewrite using ClipTranscriptSegment.createFromElements() using current segment elements and splitTime as end/start time?
    const secondSegmentFirstWord =
      segment.findFollowingTextElement(relativeSplitTime);
    const splitIndex = secondSegmentFirstWord
      ? segment.getWordsWithPunctuation().indexOf(secondSegmentFirstWord)
      : undefined;

    const firstSegmentWords = segment
      .getWordsWithPunctuation()
      .slice(0, splitIndex);

    const firstSegment = new ClipTranscriptSegment(
      firstSegmentWords,
      segment.mediaStart,
      segment.mediaStart + relativeSplitTime,
    );

    // Ensure the last word of the first segment does not extend beyond the segment timespan
    if (firstSegment.lastWord) {
      firstSegment.lastWord.relative_end_ts = Math.min(
        firstSegment.lastWord.relative_end_ts,
        relativeSplitTime,
      );
    }

    const secondSegmentWords =
      splitIndex != null
        ? segment
            .getWordsWithPunctuation()
            .slice(splitIndex)
            .map(el =>
              el.type === 'text'
                ? {
                    ...el,
                    relative_ts: Math.max(
                      0,
                      el.relative_ts - relativeSplitTime,
                    ),
                    relative_end_ts: el.relative_end_ts - relativeSplitTime,
                  }
                : el,
            )
        : [];

    const secondSegment = new ClipTranscriptSegment(
      secondSegmentWords,
      segment.mediaStart + relativeSplitTime,
      segment.mediaEnd,
    );

    return [firstSegment, secondSegment];
  }

  /**
   * Merges multiple elements into a single element, filling any gaps between them.
   * Unlike mergeElements, this allows non-contiguous elements to be merged by
   * bridging the gaps in the source media.
   *
   * Example: If element one has trim_start: 0, duration 5 and element two has
   * trim_start: 6, duration 4, there's a 1 second gap in media time (0..5_6..10).
   * This method will create a single element with trim_start: 0, duration 10.
   *
   * @param elements Array of elements to merge
   * @param targetTrack The track where the merged element will reside
   * @returns The merged Element or null if the operation fails
   */
  public bridgeAndMergeTranscriptSegmentElements(
    elements: VideoTranscriptElement[],
    targetTrack: TranscriptTrack,
  ): VideoTranscriptElement | null {
    // Validate everything except contiguity
    if (!this.trackManager.validateMerge(elements, true)) {
      console.warn('Cannot bridge and merge elements: validation failed');
      return null;
    }

    // Sort elements by time
    const sortedElements = [...elements].sort((a, b) => a.time - b.time);

    // Calculate the span from start of first to end of last (including gaps)
    const firstElement = sortedElements[0];
    const lastElement = sortedElements[sortedElements.length - 1];
    const totalSpan = lastElement.end_time - firstElement.time;

    // Replace original transcript segments with the merged element
    const firstTranscriptSegmentPosition = this.findTranscriptElementPosition(
      targetTrack,
      firstElement,
    );
    const lastTranscriptSegmentPosition = this.findTranscriptElementPosition(
      targetTrack,
      lastElement,
    );

    // Calculate properties with gap filling enabled
    const mergedVideoProps =
      this.trackManager.calculateMergedProperties<VideoTranscriptElement>(
        elements,
        true,
      );
    const mergedSegment = this.calculateMergedTranscriptSegment(
      targetTrack.transcriptSegmentElements.slice(
        firstTranscriptSegmentPosition,
        lastTranscriptSegmentPosition + 1,
      ),
    );

    // Create a new element based on the first element but with merged properties
    const mergedElement = elements[0].clone();
    mergedElement.update(mergedVideoProps);
    mergedElement.setClipTranscriptSegment(mergedSegment!);

    // Ensure the new element has a unique ID
    mergedElement.id = Element.newKey('bridged');

    // Remove range of transcript elements (video and removed fragments)
    targetTrack.removeTranscriptSegmentElements(
      firstTranscriptSegmentPosition,
      lastTranscriptSegmentPosition - firstTranscriptSegmentPosition + 1,
    );

    // Add the merged video element
    targetTrack.addTranscriptSegmentElement(
      mergedElement,
      firstTranscriptSegmentPosition,
    );

    // Calculate if subsequent elements need to be rippled
    // This happens when the new merged element covers a different total timeline span
    // than the sum of the individual element durations.  When gaps are bridged the
    // merged element becomes longer, so we need to pull all following elements *earlier*
    // by the bridged gap size so the overall timeline length remains unchanged.

    // account for existing gaps between merged elements -> should ripple by gap size less - use totalSpan not originalSpan
    const rippleAmount = mergedElement.durationSeconds - totalSpan;

    // If the merged element duration increased (gap bridged), shift subsequent elements
    // earlier by the same amount so that the end of the merged range lines up with where
    // the last original element ended.
    if (rippleAmount > 0) {
      const elementsAfter = this.trackManager.getElementsAfterTime(
        targetTrack,
        lastElement.end_time, // End of last original element
        mergedElement,
      );

      // should shift to the right instead to give space for the merged element? - done
      // Shift earlier – negative delta
      this.trackManager.applyTimeDeltaToElements(elementsAfter, rippleAmount);
    }

    return mergedElement;
  }

  /**
   * Calculates properties for the merged element
   * @param elements Array of elements to merge
   * @returns Object with properties for the merged element
   */
  private calculateMergedTranscriptSegment(
    elements: HasClipTranscriptSegment[],
  ): ClipTranscriptSegment | undefined {
    if (elements.length === 0) return;

    // Get first and last element as audio/video types
    const firstElement = elements[0];

    // takes the first element's transcript segment as base
    // adds next segments
    let mergedTranscriptSegment: ClipTranscriptSegment =
      firstElement.getClipTranscriptSegment()!;
    elements.slice(1).forEach(el => {
      const segment = el.getClipTranscriptSegment();
      if (segment) {
        if (el instanceof RemovedTranscriptClip) {
          segment?.markElementsAsRestored();
        }
        mergedTranscriptSegment = mergedTranscriptSegment.concat(segment);
      }
    });

    return mergedTranscriptSegment;
  }

  private findTranscriptElementPosition(
    track: TranscriptTrack,
    element: HasClipTranscriptSegment,
  ): number {
    if (!track) return -1;

    return track.transcriptSegmentElements.findIndex(el => el === element);
  }

  /**
   * Splits an element into two at the specified time
   * @param element The element to split
   * @param splitTime The time (in seconds) where the split occurs
   * @returns A tuple of two new Elements or null if the operation fails
   */
  public splitElement(
    element: VideoTranscriptElement,
    splitTime: number,
  ): [VideoTranscriptElement, VideoTranscriptElement] | null {
    if (!element) return null;
    if (!(element instanceof VideoTranscriptElement)) return null;

    if (!this.trackManager.validateSplit(element, splitTime)) {
      console.warn('Cannot split element: invalid split time');
      return null;
    }

    const [firstSegment, secondSegment] = this.createSplitTranscriptSegments(
      element.getClipTranscriptSegment()!,
      splitTime - element.time, // relative time
    );

    // Create new VideoTranscriptElement objects from the split segments
    const firstPartDuration = firstSegment.mediaEnd - firstSegment.mediaStart;
    const firstElement = new VideoTranscriptElement({
      ...element.getState(),
      id: `split-first-${element.id}`,
      duration: firstPartDuration,
      clipTranscriptSegment: firstSegment,
    });

    const secondPartDuration =
      secondSegment.mediaEnd - secondSegment.mediaStart;
    const secondElement = new VideoTranscriptElement({
      ...element.getState(),
      id: `split-second-${element.id}`,
      time: splitTime,
      duration: secondPartDuration,
      trim_start: secondSegment.mediaStart,
      clipTranscriptSegment: secondSegment,
    });

    // The split boundary is an interior seam: strip the enter/exit animations
    // that belong to the *other* outer edge so the new cut is a clean hard cut.
    Element.stripSeamAnimations(firstElement, { exit: true });
    Element.stripSeamAnimations(secondElement, { enter: true });

    const transcriptTrack = this.editor.getVideoTranscriptTrack();
    if (!transcriptTrack) return null;

    // Replace original transcript segments with the split elements
    const elementPosition = this.findTranscriptElementPosition(
      transcriptTrack,
      element,
    );

    if (elementPosition === -1) return null;

    // Update transcript track segment elements in correct order
    transcriptTrack.removeTranscriptSegmentElements(elementPosition, 1);
    transcriptTrack.addTranscriptSegmentElement(firstElement, elementPosition);
    transcriptTrack.addTranscriptSegmentElement(
      secondElement,
      elementPosition + 1,
    );

    return [firstElement, secondElement];
  }

  /**
   * Creates two new elements from a split operation
   * @param element The original element to split
   * @param splitTime The time where the split occurs
   * @returns Tuple of two new elements (before and after the split)
   */
  protected createSplitElements(
    element: Element,
    splitTime: number,
  ): [Element, Element] {
    const elementStartTime = element.time;
    const elementDuration = element.durationSeconds;
    const elementEndTime = elementStartTime + elementDuration;

    // Calculate durations for the two new elements
    const firstDuration = splitTime - elementStartTime;
    const secondDuration = elementEndTime - splitTime;

    // Base properties for all element types
    const baseFirstElement = element.clone();
    baseFirstElement.id = Element.newKey('split-first');
    // Use proper setters via editor
    baseFirstElement.time = elementStartTime;
    baseFirstElement.duration = firstDuration;
    if (baseFirstElement.type === 'composition') {
      pickUnderlyingVideoElement(baseFirstElement)?.update({
        id: `inner-${baseFirstElement.id}`,
      });
    }

    const baseSecondElement = element.clone();
    baseSecondElement.id = Element.newKey('split-second');
    // Use proper setters via editor
    baseSecondElement.time = splitTime;
    baseSecondElement.duration = secondDuration;
    if (baseSecondElement.type === 'composition') {
      pickUnderlyingVideoElement(baseSecondElement)?.update({
        id: `inner-${baseSecondElement.id}`,
      });
    }

    // Handle properties that are specific to video/audio elements
    if (
      element.type === 'video' ||
      element.type === 'audio' ||
      element.type === 'composition'
    ) {
      const mediaElement = element as VideoElement | AudioElement;

      // Update trim_start for second element
      const trimSourceElement = pickUnderlyingVideoElement(mediaElement);
      const firstTrimEnd = (trimSourceElement?.trim_start ?? 0) + firstDuration;
      const trimDestinationElement =
        pickUnderlyingVideoElement(baseSecondElement);
      trimDestinationElement?.update({ trim_start: firstTrimEnd } as Element);

      (baseFirstElement as VideoElement | AudioElement).audio_fade_out = 0;
      (baseSecondElement as VideoElement | AudioElement).audio_fade_in = 0;

      // Ensure transcript segment is refreshed
      // TODO: Do we need to do thius?
      // videoElement.invalidateTranscriptSegment();
    }

    Element.stripSeamAnimations(baseFirstElement, { exit: true });
    Element.stripSeamAnimations(baseSecondElement, { enter: true });

    return [baseFirstElement, baseSecondElement];
  }

  /**
   * Inserts a transcript segment as a new video element, handling segment splitting and positioning
   * @param clipTranscriptSegment The segment to insert
   * @param sourceElement Element to clone properties from (optional)
   * @param insertTime Timeline position for the new element
   * @param transcriptTrack The transcript track to modify
   * @param segmentPosition Position in transcript segment elements array
   * @param fromIndex Start index in original segment
   * @param toIndex End index in original segment
   * @returns The inserted VideoTranscriptElement
   */
  public insertTranscriptSegmentAsVideoElement(
    clipTranscriptSegment: ClipTranscriptSegment,
    sourceElement: VideoTranscriptElement | null,
    insertTime: number,
    transcriptTrack: TranscriptTrack,
    segmentPosition: number,
    fromIndex: number,
    toIndex: number,
    mode: TimelineMode = TimelineMode.RIPPLE,
  ): VideoTranscriptElement {
    clipTranscriptSegment.markElementsAsRestored();

    // Create new video element
    const newElement = sourceElement
      ? sourceElement.clone()
      : new VideoTranscriptElement({ clipTranscriptSegment });

    // Set element properties
    newElement.update({
      id: `inserted-${uuid()}`,
      time: insertTime,
      trim_start: clipTranscriptSegment.mediaStart,
      duration:
        clipTranscriptSegment.mediaEnd - clipTranscriptSegment.mediaStart,
      clipTranscriptSegment: clipTranscriptSegment,
    });

    // A restored run is reinserted into the interior of the content (between the
    // still-removed parts), so both its edges are seams: drop the enter/exit
    // animations cloned from the source so it does not inherit the video fade.
    Element.stripSeamAnimations(newElement, { enter: true, exit: true });

    // Get original removed segment
    const removedTranscriptClip = transcriptTrack.transcriptSegmentElements[
      segmentPosition
    ] as RemovedTranscriptClip;

    // Create split segments
    const { firstPart, secondPart } = this.createSplitRemovedSegments(
      removedTranscriptClip,
      clipTranscriptSegment,
      fromIndex,
      toIndex,
    );

    // Replace original segment with three parts: first removed, inserted video, second removed
    transcriptTrack.removeTranscriptSegmentElements(segmentPosition, 1);

    // Add segments in reverse order to maintain correct positions
    if (
      secondPart.getClipTranscriptSegment()!.getWordsWithPunctuation().length >
      0
    ) {
      transcriptTrack.addTranscriptSegmentElement(secondPart, segmentPosition);
    }

    transcriptTrack.addTranscriptSegmentElement(newElement, segmentPosition);

    if (
      firstPart.getClipTranscriptSegment()!.getWordsWithPunctuation().length > 0
    ) {
      transcriptTrack.addTranscriptSegmentElement(firstPart, segmentPosition);
    }

    if (mode === TimelineMode.RIPPLE) {
      // Get all elements that start after the removed element's start time
      const elementsAfter = this.trackManager.getElementsAfterTime(
        transcriptTrack,
        newElement.time,
        newElement,
      );
      // Shift them earlier by the duration of the removed element
      this.trackManager.applyTimeDeltaToElements(
        elementsAfter,
        newElement.durationSeconds,
      );
    }

    return newElement;
  }

  /**
   * Creates split removed segments from original segment
   */
  private createSplitRemovedSegments(
    removedTranscriptClip: RemovedTranscriptClip,
    restoredSegment: ClipTranscriptSegment,
    fromIndex: number,
    toIndex: number,
  ): { firstPart: RemovedTranscriptClip; secondPart: RemovedTranscriptClip } {
    const originalSegment = removedTranscriptClip.getClipTranscriptSegment()!;
    const wordsWithPunctuation = originalSegment.getWordsWithPunctuation();

    // First part: words before the restored range
    const firstSegmentWords = wordsWithPunctuation.slice(0, fromIndex);
    const firstSegment = new ClipTranscriptSegment(
      firstSegmentWords,
      removedTranscriptClip.getMediaStartTime(),
      restoredSegment.mediaStart,
    );

    // Second part: words after the restored range
    const secondSegmentWords = wordsWithPunctuation
      .slice(toIndex + 1)
      .map(el =>
        el.type === 'text'
          ? {
              ...el,
              relative_ts: el.media_ts - restoredSegment.mediaEnd,
              relative_end_ts: el.media_end_ts - restoredSegment.mediaEnd,
            }
          : el,
      );
    const secondSegment = new ClipTranscriptSegment(
      secondSegmentWords,
      restoredSegment.mediaEnd,
      removedTranscriptClip.getMediaEndTime(),
    );

    // Create cloned removed clips with new segments
    const firstPart = removedTranscriptClip.clone();
    firstPart.setClipTranscriptSegment(firstSegment);

    const secondPart = removedTranscriptClip.clone();
    secondPart.setClipTranscriptSegment(secondSegment);

    return { firstPart, secondPart };
  }

  // -------------------------------------------------------------------
  // Methods for TranscriptSimpleRemoveCommand
  // -------------------------------------------------------------------

  /**
   * Remove a transcript element and leave a RemovedTranscriptClip placeholder.
   *
   * `preserveTokenIds` (default true) keeps the placeholder's tokens by
   * reference so Restore brings them back with stable ids. Move paths
   * pass false so the placeholder gets fresh ids and can coexist with a
   * re-inserted VTE that keeps the original ids.
   */
  removeElement(
    element: VideoTranscriptElement,
    mode: TimelineMode = TimelineMode.DEFAULT,
    options: { preserveTokenIds?: boolean } = {},
  ): boolean {
    const track = this.editor.timeline.getTrackForElement(element.id);
    if (!element || !track || !(track instanceof TranscriptTrack)) return false;

    const removeSuccess = track.removeElement(element.id, {
      preserveTokenIds: options.preserveTokenIds,
    });
    if (!removeSuccess) return false;

    const startTime = element.time;
    const duration = element.durationSeconds;
    // Handle ripple mode if duration is positive
    if (mode === TimelineMode.RIPPLE && duration > 0) {
      // Get all elements that start after the removed element's start time
      const elementsAfter = this.trackManager.getElementsAfterTime(
        track,
        startTime,
        element,
      );
      // Shift them earlier by the duration of the removed element
      this.trackManager.applyTimeDeltaToElements(elementsAfter, -duration);
    }

    // Update the timeline duration
    this.editor.timeline.recalculateDuration();

    // Mark the timeline as dirty
    this.trackManager.markDirty(`removeElement (${element.id})`);

    // Notify any observers of the change
    this.trackManager.notifyObservers(EditorChangeType.ELEMENT_REMOVED, {
      elementId: element.id,
      mode: mode,
    });

    return true;
  }

  /**
   * Calculates and updates transcript segments after element trim start operation
   * @param element Element that was trimmed (should have updated video properties)
   * @param originalSegment Original transcript segment before trim
   * @param track Transcript track containing the element
   * @returns true if successful
   */
  private calculateAndUpdateTranscriptSegmentsForTrimStart(
    element: VideoTranscriptElement,
    originalSegment: ClipTranscriptSegment,
    track: TranscriptTrack,
  ): ClipTranscriptSegment | null {
    // Get the actual trim start from the updated video element
    const newMediaTrimStart = element.getMediaStartTime();

    // Calculate split time based on difference from original segment start
    const splitTime = newMediaTrimStart - originalSegment.mediaStart;

    // Split the original segment using the actual updated trim start
    const [segmentToRemove, segmentToKeep] = this.createSplitTranscriptSegments(
      originalSegment,
      splitTime,
    );

    // Find element position in transcript track
    const elementPosition = this.findTranscriptElementPosition(track, element);
    if (elementPosition === -1) return null;

    // Update element with new segment
    element.setClipTranscriptSegment(segmentToKeep);

    // Try to merge with previous removed clip using concat
    const prevElement = track.transcriptSegmentElements.at(elementPosition - 1);
    const prevSegment = prevElement?.getClipTranscriptSegment();
    if (
      prevElement instanceof RemovedTranscriptClip &&
      Math.abs(prevSegment!.mediaEnd - segmentToRemove.mediaStart) < 0.001
    ) {
      // Merge with previous removed clip using concat
      const mergedSegment = prevSegment!.concat(segmentToRemove);
      const mergedClip = new RemovedTranscriptClip({
        clipTranscriptSegment: mergedSegment,
      });

      // Replace previous clip with merged clip
      track.removeTranscriptSegmentElements(elementPosition - 1, 1);
      track.addTranscriptSegmentElement(mergedClip, elementPosition - 1);
    } else {
      // Insert new removed clip before current element
      const removedClip = new RemovedTranscriptClip({
        clipTranscriptSegment: segmentToRemove,
      });
      track.addTranscriptSegmentElement(removedClip, elementPosition);
    }

    return new ClipTranscriptSegment(
      segmentToRemove.getWordsWithPunctuation(),
      segmentToRemove.mediaStart,
      segmentToRemove.mediaEnd,
    );
  }

  /**
   * Excise middle section from transcript element, creating removed clip for excised portion
   * Similar to createSplitRemovedSegments but for splitting a video element
   */
  exciseFromTranscriptElement(
    element: VideoTranscriptElement,
    mediaStartTime: number,
    mediaEndTime: number,
    track: TranscriptTrack,
    mode: TimelineMode = TimelineMode.DEFAULT,
  ): {
    firstPart: VideoTranscriptElement;
    secondPart: VideoTranscriptElement;
    removedSegment: ClipTranscriptSegment;
  } | null {
    if (!element || !track) return null;

    // Find element position in transcript track
    const elementPosition = this.findTranscriptElementPosition(track, element);
    if (elementPosition === -1) return null;

    const originalSegment = element.getClipTranscriptSegment();
    const originalTrimStart = element.trim_start ?? 0;

    // Validate excision range
    if (
      mediaStartTime >= mediaEndTime ||
      mediaStartTime < originalTrimStart ||
      mediaEndTime > originalSegment.mediaEnd
    ) {
      return null;
    }

    // Use createSplitTranscriptSegments similar to insertTranscriptSegmentAsVideoElement
    const [firstSegmentToKeep, segmentToTrim] =
      this.createSplitTranscriptSegments(
        originalSegment,
        mediaStartTime - originalSegment.mediaStart,
      );

    const [segmentToExcise, secondSegmentToKeep] =
      this.createSplitTranscriptSegments(
        segmentToTrim,
        mediaEndTime - mediaStartTime,
      );

    const removedClip = new RemovedTranscriptClip({
      clipTranscriptSegment: segmentToExcise,
    });

    // Create first part (before excision)
    const firstPartDuration =
      firstSegmentToKeep.mediaEnd - firstSegmentToKeep.mediaStart;
    const firstPart = new VideoTranscriptElement({
      ...element.getState(),
      id: `excised-first-${element.id}`,
      duration: firstPartDuration,
      clipTranscriptSegment: firstSegmentToKeep,
    });

    const secondPartDuration =
      secondSegmentToKeep.mediaEnd - secondSegmentToKeep.mediaStart;
    const excisedDuration = mediaEndTime - mediaStartTime;
    const secondPartStartTime = element.time + firstPartDuration;

    const secondPart = new VideoTranscriptElement({
      ...element.getState(),
      id: `excised-second-${element.id}`,
      time: secondPartStartTime,
      duration: secondPartDuration,
      trim_start: secondSegmentToKeep.mediaStart,
      clipTranscriptSegment: secondSegmentToKeep,
    });

    // The excised range sits between the two kept parts, so both their inner
    // edges are interior seams: strip the enter/exit animations that belong to
    // the other (outer) edge so the deletion boundary is a clean hard cut.
    Element.stripSeamAnimations(firstPart, { exit: true });
    Element.stripSeamAnimations(secondPart, { enter: true });

    // Update transcript track segment elements in correct order
    track.removeTranscriptSegmentElements(elementPosition, 1);
    track.addTranscriptSegmentElement(firstPart, elementPosition);
    track.addTranscriptSegmentElement(removedClip, elementPosition + 1);
    track.addTranscriptSegmentElement(secondPart, elementPosition + 2);

    // Handle ripple effects if in RIPPLE mode
    if (mode === TimelineMode.RIPPLE) {
      const elementsAfter = this.trackManager.getElementsAfterTime(
        track,
        secondPart.end_time,
        secondPart,
      );
      this.trackManager.applyTimeDeltaToElements(
        elementsAfter,
        -excisedDuration,
      );
    }

    return {
      firstPart,
      secondPart,
      removedSegment: new ClipTranscriptSegment(
        segmentToExcise.getWordsWithPunctuation(),
        segmentToExcise.mediaStart,
        segmentToExcise.mediaEnd,
      ),
    };
  }

  /**
   * Marks punctuation tokens within a transcript segment as removed
   * @param element The VideoTranscriptElement containing the punctuation
   * @param fromIndex Starting index in the segment's words (including punctuation)
   * @param toIndex Ending index in the segment's words (including punctuation)
   * @returns true if successful, false otherwise
   */
  public removePunctuation(
    element: VideoTranscriptElement,
    fromIndex: number,
    toIndex: number,
  ): ClipTranscriptSegment | null {
    const segment = element.getClipTranscriptSegment();
    if (!segment) return null;
    const result = removePunctuationRange(segment, fromIndex, toIndex);
    if (result.ok && result.value.summary.removedCount > 0) {
      element.setClipTranscriptSegment(result.value.segment);
      this.editor.markDirty(EditorChangeType.ELEMENT_UPDATED);
      return result.value.segment;
    }
    if (!result.ok) {
      LogManager.getLogger('transcript:punctuation').warn(
        'removePunctuation failed',
        {
          fromIndex,
          toIndex,
          error: result.error,
          diagnostics: result.diagnostics,
        },
      );
    }
    return null;
  }

  /**
   * Restores previously removed punctuation tokens within a transcript segment
   * @param element The element containing the punctuation (must be VideoTranscriptElement)
   * @param fromIndex Starting index in the segment's words (including punctuation)
   * @param toIndex Ending index in the segment's words (including punctuation)
   * @returns true if successful, false otherwise
   */
  public restorePunctuation(
    element: HasClipTranscriptSegment,
    fromIndex: number,
    toIndex: number,
  ): boolean {
    const segment = element.getClipTranscriptSegment();
    if (!segment) return false;
    const result = restorePunctuationRange(segment, fromIndex, toIndex);
    if (result.ok && result.value.summary.restoredCount > 0) {
      if (
        element instanceof VideoTranscriptElement ||
        element instanceof RemovedTranscriptClip
      ) {
        element.setClipTranscriptSegment(result.value.segment);
      }
      this.editor.markDirty(EditorChangeType.ELEMENT_UPDATED);
      return true;
    }
    if (!result.ok) {
      LogManager.getLogger('transcript:punctuation').warn(
        'restorePunctuation failed',
        {
          fromIndex,
          toIndex,
          error: result.error,
          diagnostics: result.diagnostics,
        },
      );
    }
    return false;
  }

  /**
   * Inserts transcript segments as new video elements at a specific timeline position
   * Reuses existing insertion logic and handles ripple effects
   *
   * @param segmentsToInsert Array of transcript segments to insert
   * @param insertionTime Timeline position where elements should be inserted
   * @param sourceElement Optional element to clone properties from
   * @param mode Timeline mode for handling overlaps and ripple effects
   * @returns Array of inserted VideoTranscriptElements or null if failed
   */
  public insertTranscriptSegmentsAtTime(
    segmentsToInsert: ClipTranscriptSegment[],
    insertionTime: number,
    sourceElement: VideoTranscriptElement,
    mode: TimelineMode = TimelineMode.RIPPLE,
    options: { preserveTokenIds?: boolean } = {},
  ): VideoTranscriptElement[] | null {
    if (!segmentsToInsert.length) return null;

    const transcriptTrack = this.editor.getVideoTranscriptTrack();
    if (!transcriptTrack) return null;

    const insertedElements: VideoTranscriptElement[] = [];
    let currentTime = insertionTime;

    // Create video elements from segments
    for (const segment of segmentsToInsert) {
      // Always clone the segment + tokens so in-place mutations (markAsAdded,
      // unmarkElements, split) can't leak to the source the caller may hold
      // for undo. preserveTokenIds keeps ids (move path) vs regenerates them
      // (paste path, where the source may still be on the track).
      const tokenGenerator = IdGenerator.get(
        IdGeneratorEntityType.TranscriptToken,
        'hex',
      );
      const segmentForInsertion = new ClipTranscriptSegment(
        segment.getWordsWithPunctuation().map(el => ({
          ...el,
          id: options.preserveTokenIds ? el.id : tokenGenerator.getNextIdHex(),
        })),
        segment.mediaStart,
        segment.mediaEnd,
      );
      const element = sourceElement
        ? sourceElement.clone()
        : new VideoTranscriptElement({
            clipTranscriptSegment: segmentForInsertion,
          });

      const duration =
        segmentForInsertion.mediaEnd - segmentForInsertion.mediaStart;

      element.update({
        id: `inserted-${uuid()}`,
        time: currentTime,
        trim_start: segmentForInsertion.mediaStart,
        duration: duration,
        clipTranscriptSegment: segmentForInsertion,
      });

      insertedElements.push(element);
      currentTime += duration;
    }

    // Find insertion position in transcript track
    const insertionIndex = this.findInsertionIndex(
      transcriptTrack.transcriptSegmentElements,
      insertionTime,
    );

    // Insert elements into track
    insertedElements.forEach((element, offset) => {
      transcriptTrack.addTranscriptSegmentElement(
        element,
        insertionIndex + offset,
      );
    });

    // Calculate total duration for ripple effects
    const totalDuration = segmentsToInsert.reduce(
      (sum, segment) => sum + (segment.mediaEnd - segment.mediaStart),
      0,
    );

    // Handle ripple effects if in RIPPLE mode
    if (mode === TimelineMode.RIPPLE && totalDuration > 0) {
      // Get elements after the insertion point, excluding the inserted elements
      // Use the first inserted element as the exclude element for getElementsAfterTime
      const excludeElement = insertedElements[0];
      const elementsAfter = this.trackManager
        .getElementsAfterTime(transcriptTrack, insertionTime, excludeElement)
        .filter(el => !insertedElements.includes(el as VideoTranscriptElement));
      this.trackManager.applyTimeDeltaToElements(elementsAfter, totalDuration);
    }

    // Update timeline duration
    this.editor.timeline.recalculateDuration();
    this.trackManager.markDirty('insertTranscriptSegmentsAtTime');

    return insertedElements;
  }

  /**
   * Inserts transcript segments by splitting an existing video element at a specific word index
   * Reuses existing splitElement method and insertion logic
   *
   * @param targetElement The video element to split for insertion
   * @param splitAtWordIndex The word index where to split (insertion happens after this index)
   * @param segmentsToInsert Array of transcript segments to insert
   * @param mode Timeline mode for handling ripple effects
   * @returns Object with split parts and inserted elements, or null if failed
   */
  public insertTranscriptSegmentsBySplitting(
    targetElement: VideoTranscriptElement,
    mediaSplitTime: number,
    segmentsToInsert: ClipTranscriptSegment[],
    mode: TimelineMode = TimelineMode.RIPPLE,
  ): {
    firstPart: VideoTranscriptElement;
    insertedElements: VideoTranscriptElement[];
    secondPart: VideoTranscriptElement;
  } | null {
    if (!segmentsToInsert.length) return null;

    const segment = targetElement.getClipTranscriptSegment();

    // Calculate timeline split time
    const relativeSplitTime = mediaSplitTime - segment.mediaStart;
    const splitTime = targetElement.time + relativeSplitTime;

    // Use existing split functionality
    const splitResult = this.splitElement(targetElement, splitTime);
    if (!splitResult) return null;

    const [firstPart, secondPart] = splitResult;

    // Insert segments between the split parts using existing insertion method
    const insertedElements = this.insertTranscriptSegmentsAtTime(
      segmentsToInsert,
      splitTime,
      targetElement,
      mode,
    );

    if (!insertedElements) return null;

    return {
      firstPart,
      insertedElements,
      secondPart,
    };
  }

  /**
   * Finds the appropriate index in the transcript track to insert an element at a given time
   */
  private findInsertionIndex(
    elements: readonly HasClipTranscriptSegment[],
    insertionTime: number,
    fallback?: number,
  ): number {
    const index = elements.findIndex(
      el => el instanceof VideoTranscriptElement && el.time >= insertionTime,
    );
    return index !== -1 ? index : (fallback ?? elements.length);
  }
}
